/**
 * Program 12.1 UX4 — Bid decision dimension display (factors_json).
 * FACT / CALCULATION / HUMAN ASSESSMENT — no fabricated numeric overall score.
 */
export type BidDimensionKind = 'FACT' | 'CALCULATION' | 'HUMAN_ASSESSMENT' | 'AI_INFERENCE';

export type BidDimensionRow = {
  key: string;
  label: string;
  kind: BidDimensionKind;
  value?: string;
  score?: number;
  maxScore?: number;
  notes?: string;
};

export const RFQ_SHORT_DIMENSIONS: Array<{ key: string; label: string }> = [
  { key: 'scope_fit', label: 'Scope fit' },
  { key: 'price_competitiveness', label: 'Price competitiveness' },
  { key: 'delivery_capacity', label: 'Capacity to deliver' },
];

export const DEFAULT_BID_DIMENSIONS: Array<{ key: string; label: string }> = [
  { key: 'strategic_fit', label: 'Strategic Fit' },
  { key: 'customer_relationship', label: 'Customer Relationship' },
  { key: 'technical_capability', label: 'Technical Capability' },
  { key: 'available_capacity', label: 'Available Capacity' },
  { key: 'commercial_attractiveness', label: 'Commercial Attractiveness' },
  { key: 'schedule_feasibility', label: 'Schedule Feasibility' },
  { key: 'contract_risk', label: 'Contract Risk' },
  { key: 'cash_flow_exposure', label: 'Cash-Flow Exposure' },
  { key: 'competition', label: 'Competition' },
  { key: 'win_probability', label: 'Win Probability' },
  { key: 'resource_commitment', label: 'Resource Commitment' },
  { key: 'country_compliance_risk', label: 'Country / Compliance Risk' },
];

function parseFactors(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return raw as Record<string, unknown>;
}

export function parseBidDimensions(factorsJson: unknown): BidDimensionRow[] {
  const factors = parseFactors(factorsJson);
  const rows: BidDimensionRow[] = [];
  const defs =
    factors.factor_profile === 'RFQ_SHORT' ? RFQ_SHORT_DIMENSIONS : DEFAULT_BID_DIMENSIONS;

  for (const def of defs) {
    const entry = factors[def.key];
    if (entry == null) {
      rows.push({ key: def.key, label: def.label, kind: 'HUMAN_ASSESSMENT' });
      continue;
    }
    if (typeof entry === 'string') {
      rows.push({
        key: def.key,
        label: def.label,
        kind: 'HUMAN_ASSESSMENT',
        value: entry,
      });
      continue;
    }
    if (typeof entry === 'object') {
      const o = entry as Record<string, unknown>;
      const score = o.score != null ? Number(o.score) : undefined;
      const max = o.max != null ? Number(o.max) : o.maxScore != null ? Number(o.maxScore) : undefined;
      rows.push({
        key: def.key,
        label: def.label,
        kind: score != null && Number.isFinite(score) ? 'CALCULATION' : 'HUMAN_ASSESSMENT',
        value: o.value != null ? String(o.value) : o.rating != null ? String(o.rating) : undefined,
        score: Number.isFinite(score!) ? score : undefined,
        maxScore: Number.isFinite(max!) ? max : undefined,
        notes: o.notes != null ? String(o.notes) : undefined,
      });
    }
  }

  return rows;
}

export function parseConditionalConditions(factorsJson: unknown): string[] {
  const factors = parseFactors(factorsJson);
  const raw = factors.conditions ?? factors.conditional_conditions;
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string') return raw.split('\n').map((s) => s.trim()).filter(Boolean);
  return [];
}

export function hasDeterministicOverallScore(rows: BidDimensionRow[]): boolean {
  const scored = rows.filter((r) => r.score != null && r.maxScore != null);
  return scored.length >= 3;
}

export function computeOverallScore(rows: BidDimensionRow[]): { total: number; max: number } | null {
  if (!hasDeterministicOverallScore(rows)) return null;
  let total = 0;
  let max = 0;
  for (const r of rows) {
    if (r.score != null && r.maxScore != null) {
      total += r.score;
      max += r.maxScore;
    }
  }
  return max > 0 ? { total, max } : null;
}

export function outcomeDisplayLabel(outcome: string): string {
  const o = outcome.toUpperCase();
  if (o === 'GO') return 'BID';
  if (o === 'NO_GO') return 'NO-BID';
  if (o === 'CONDITIONAL') return 'CONDITIONAL BID';
  return o || 'PENDING';
}

export function qualitativeRecommendation(outcome: string): string | null {
  const o = outcome.toUpperCase();
  if (o === 'GO') return 'Recommendation: BID';
  if (o === 'NO_GO') return 'Recommendation: NO-BID';
  if (o === 'CONDITIONAL') return 'Recommendation: CONDITIONAL BID';
  return null;
}

export function mergeFactorsPatch(
  existing: unknown,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const base = parseFactors(existing);
  return { ...base, ...patch };
}

export function dimensionAssessmentPatch(key: string, value: string): Record<string, unknown> {
  return { [key]: { value, rating: value } };
}
